import type { LibraryProduct } from '@courses/types-layer/types/library'

/** Tag categories surfaced on the course card, in display order. */
export type CourseCardTagCategory = 'language' | 'difficulty' | 'topic'

const CATEGORY_ORDER: CourseCardTagCategory[] = ['language', 'difficulty', 'topic']

/** Categories whose display label is a stable value in library locale files. Topic labels stay as authored. */
const LOCALIZED_TAG_CATEGORIES = new Set<CourseCardTagCategory>(['language', 'difficulty'])

export interface CourseCardTag {
  category: CourseCardTagCategory
  label: string
  value?: string
}

/**
 * One labelled tag per known category (language → difficulty → topic) for compact display
 * on the course card. Categories without a usable label are skipped.
 */
export const getCourseCardTags = (product: LibraryProduct): CourseCardTag[] => {
  const tags = product.tags ?? []

  return CATEGORY_ORDER.reduce<CourseCardTag[]>((acc, category) => {
    const match = tags.find(tag => tag.category === category && Boolean(tag.label?.trim()))
    const label = match?.label?.trim()
    if (!label || !match) return acc

    const value = match.value?.trim()
    acc.push(value ? { category, label, value } : { category, label })
    return acc
  }, [])
}

/** Short difficulty values seen in tag payloads, mapped to the locale keys under `tags.difficulty`. */
const DIFFICULTY_VALUE_ALIASES: Record<string, string> = {
  beg: 'beginner',
  int: 'intermediate',
  adv: 'advanced',
}

/** English language names sent as tag values (e.g. `italian`), mapped to the locale keys under `tags.language`. */
const LANGUAGE_VALUE_ALIASES: Record<string, string> = {
  english: 'en',
  spanish: 'es',
  french: 'fr',
  portuguese: 'pt',
  german: 'de',
  dutch: 'nl',
  italian: 'it',
  danish: 'da',
  norwegian: 'no',
  finnish: 'fi',
  swedish: 'sv',
}

/**
 * Difficulty values are case-insensitive enums. Language values may be English names (`italian`)
 * or codes; codes keep their casing (`pt-BR`, `fr-CA`).
 */
const normalizeTagValue = (category: CourseCardTagCategory, value: string): string => {
  const lowered = value.toLowerCase()

  if (category === 'language') return LANGUAGE_VALUE_ALIASES[lowered] ?? value
  if (category === 'difficulty') return DIFFICULTY_VALUE_ALIASES[lowered] ?? lowered
  return value
}

/** Locale key for a tag value, e.g. `library.myCourseCard.tags.language.it`. */
export const courseCardTagMessageKey = (tag: CourseCardTag): string | null => {
  if (!LOCALIZED_TAG_CATEGORIES.has(tag.category)) return null

  const value = tag.value?.trim()
  if (!value) return null

  return `library.myCourseCard.tags.${tag.category}.${normalizeTagValue(tag.category, value)}`
}

/**
 * Localized label for a course card tag.
 * Language and difficulty values map to `library.myCourseCard.tags.*` in the active locale.
 * Missing keys and topic tags keep the API label.
 */
export const resolveCourseCardTagLabel = (tag: CourseCardTag, t: (key: string) => string, te: (key: string) => boolean): string => {
  const key = courseCardTagMessageKey(tag)
  if (key && te(key)) return t(key)

  return tag.label.trim()
}
